import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import toast from "react-hot-toast";
import { storeAuthTokens } from "../../utils/storeAuthTokens";

const RootGoogleHandler = () => {
  const navigate = useNavigate();

  const isMountedRef = useRef(true);

  useEffect(() => {
    let isExecuted = false;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    const goToDashboard = () => {
      if (isMountedRef.current) {
        navigate("/dashboard", { replace: true });
      }
    };

    // Only handle OAuth callback on root path, not on /login or /callback routes
    const isRootPath = window.location.pathname === '/';
    if (!isRootPath) {
      return;
    }

    // No code: normal app entry
    if (!code) {
      goToDashboard();
      return;
    }

    // Check if we've already processed this code (prevent React double invocation)
    const processedCode = sessionStorage.getItem('google_oauth_processed_code');
    if (processedCode === code) {
      goToDashboard();
      return;
    }

    // Have code: complete Google login first
    (async () => {
      if (isExecuted || !isMountedRef.current) return;
      isExecuted = true;

      try {
        // Mark code as being processed
        sessionStorage.setItem('google_oauth_processed_code', code);

        const res = await axiosInstance.get("/google/callback", { params: { code } });
        const { user, token, refreshToken, googleTokens, idTokenPayload } = res.data || {};

        if (!user) {
          toast.error("Login failed: user not found");
          sessionStorage.removeItem('google_oauth_processed_code');
          goToDashboard();
          return;
        }

        storeAuthTokens({ user, token, refreshToken, googleTokens, idTokenPayload });

        // Remove ?code from URL and clear processed code
        window.history.replaceState(null, "", window.location.pathname);
        sessionStorage.removeItem('google_oauth_processed_code');

        // navigate is router state, not React state — safe to call regardless of mount
        if (isMountedRef.current) {
          toast.success("You logged in successfully");
        }
        navigate("/dashboard", { replace: true });
      } catch (err: any) {
        if (!isMountedRef.current) return;
        console.error('Google authentication error:', err);
        // Clear the processed code on failure so user can try again
        sessionStorage.removeItem('google_oauth_processed_code');
        toast.error("Failed to complete Google login. Please try again.");
        goToDashboard();
      }
    })();

    return () => {
      isMountedRef.current = false;
    };
  }, [navigate]);

  return <div>Completing Google sign-in…</div>;
};

export default RootGoogleHandler;